import { createContext, type ReactNode, useMemo } from 'react';
import { useActorRef } from '@xstate/react';
import type { AnyActor } from 'xstate';

import {
	useGlobalLinks,
	useIsEditing,
	useMarketCode,
	useRequiredContext,
	useSitecoreContext,
} from 'hooks';
import {
	type SessionMachineActor,
	useSessionMachine,
} from 'state-machines/session';

interface GlobalMachinesContextType {
	sessionService: SessionMachineActor;
}

export const GlobalMachinesContext = createContext<
	GlobalMachinesContextType | undefined
>(undefined);

interface Props {
	children: ReactNode;
}

// TODO: There are no more global machines, plural, so rename, remove/use xtate machine context helper.
export function GlobalMachinesContextProvider({ children }: Props) {
	const marketCode = useMarketCode();
	const isEditing = useIsEditing();
	const globalLinks = useGlobalLinks();
	const { sitecoreContext } = useSitecoreContext();
	const sessionMachine = useSessionMachine();
	const juridicalType = sitecoreContext?.juridicalType;

	const sessionService = useActorRef(sessionMachine, {
		input: {
			skipAuth: isEditing,
			organizationDetailUrl: globalLinks?.accountDetailsPro || '',
			juridicalType,
			marketCode,
		},
	});

	const globalMachinesContextValue: Record<
		keyof GlobalMachinesContextType,
		AnyActor
	> = useMemo(
		() => ({
			sessionService,
		}),
		[sessionService],
	);
	return (
		<GlobalMachinesContext value={globalMachinesContextValue}>
			{children}
		</GlobalMachinesContext>
	);
}
GlobalMachinesContextProvider.displayName = 'GlobalMachinesContextProvider';

export function useGlobalMachinesContext() {
	return useRequiredContext(GlobalMachinesContext, 'GlobalMachinesContext');
}
